const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

export const INITIAL_PARTENAIRE_FORM = {
  nom: "",
  photo_url: "",
  imageFile: null,
  link_url: "",
  ordre: 1,
  is_active: true,
};

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (user) => Number(user?.role_id) === 4;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

export const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const getMediaUrl = (url) => {
  if (!url) return "";
  if (isExternalURL(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

export const getDisplayUrl = (url) => {
  if (!url) return "";

  const trimmed = String(url).trim();

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

export const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const getStatusLabel = (isActive) => (isActive ? "Actif" : "Inactif");

export const getStatusClass = (isActive) =>
  isActive
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";

export const filterAndSortPartenaires = (partenaires = [], filters = {}) => {
  const query = normalizeText(filters.query);
  const status = filters.status || "all";

  let result = [...partenaires];

  if (status === "active") {
    result = result.filter((partenaire) => Boolean(partenaire.is_active));
  }

  if (status === "inactive") {
    result = result.filter((partenaire) => !Boolean(partenaire.is_active));
  }

  if (query) {
    result = result.filter((partenaire) => {
      const haystack = normalizeText(
        [partenaire.nom, partenaire.link_url].filter(Boolean).join(" ")
      );

      return haystack.includes(query);
    });
  }

  result.sort((a, b) => {
    if (filters.sortOrder === "ordre_desc") {
      return Number(b.ordre || 0) - Number(a.ordre || 0);
    }

    if (filters.sortOrder === "name_asc") {
      return normalizeText(a.nom).localeCompare(normalizeText(b.nom));
    }

    if (filters.sortOrder === "name_desc") {
      return normalizeText(b.nom).localeCompare(normalizeText(a.nom));
    }

    return Number(a.ordre || 0) - Number(b.ordre || 0);
  });

  return result;
};

export const buildPartenairesStats = (
  partenaires = [],
  filteredPartenaires = []
) => {
  const active = partenaires.filter((partenaire) =>
    Boolean(partenaire.is_active)
  ).length;

  const inactive = partenaires.length - active;

  const withLogo = partenaires.filter((partenaire) =>
    Boolean(partenaire.photo_url)
  ).length;

  const withLink = partenaires.filter((partenaire) =>
    Boolean(partenaire.link_url)
  ).length;

  const maxOrder = partenaires.reduce((max, partenaire) => {
    const ordre = Number(partenaire.ordre || 0);
    return ordre > max ? ordre : max;
  }, 0);

  return {
    total: partenaires.length,
    filtered: filteredPartenaires.length,
    active,
    inactive,
    withLogo,
    withLink,
    maxOrder,
  };
};

export const normalizePartenairePayload = (formData) => ({
  ...formData,
  nom: String(formData.nom || "").trim(),
  photo_url: String(formData.photo_url || "").trim(),
  link_url: String(formData.link_url || "").trim(),
  ordre:
    formData.ordre === "" || formData.ordre === null || formData.ordre === undefined
      ? 1
      : Number(formData.ordre),
  is_active: Boolean(formData.is_active),
});

export const validatePartenaireForm = (formData) => {
  if (!String(formData.nom || "").trim()) {
    return "Le nom du partenaire est obligatoire.";
  }

  if (formData.ordre !== "" && Number(formData.ordre) < 0) {
    return "L'ordre d'affichage doit être un nombre positif.";
  }

  return null;
};

export const buildInitialFormFromPartenaire = (partenaire) => {
  if (!partenaire) return INITIAL_PARTENAIRE_FORM;

  return {
    ...INITIAL_PARTENAIRE_FORM,
    ...partenaire,
    imageFile: null,
    is_active: Boolean(partenaire.is_active),
    ordre: partenaire.ordre ?? 1,
  };
};